import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import type {TaillightsController} from '@github-ui/taillights/controller'
import {useTaillightsController} from '@github-ui/taillights/provider'
import {clsx} from 'clsx'
import {Suspense, use, useDeferredValue, useEffect, useState} from 'react'

import type {CodeNavSymbolDetailsLoaderProps} from '../blob/BlobContent/CodeNav/CodeNavSymbolDetails'
import detailsStyles from '../blob/BlobContent/CodeNav/CodeNavSymbolDetails.module.css'
import {CodeNavSymbolDetailsView} from '../blob/BlobContent/CodeNav/CodeNavSymbolDetailsView'
import {useCodeBlobStartupError} from './source-context'
import styles from './TaillightsCodeNavSymbolDetails.module.css'

type Selection = Pick<CodeNavSymbolDetailsLoaderProps, 'codeNavInfo' | 'selectedText' | 'lineNumber' | 'offset'>
type Request = ReturnType<typeof createRequest>

async function settle<T>(promise: Promise<T>, onFailure?: () => void): Promise<PromiseSettledResult<T>> {
  // Unselected requests may reject after their controller is disposed. Keep the error for the reader.
  try {
    return {status: 'fulfilled', value: await promise}
  } catch (reason) {
    onFailure?.()
    return {status: 'rejected', reason}
  }
}

function createRequest(controller: TaillightsController, selection: Selection, key: number, revision: number) {
  const {codeNavInfo, selectedText, lineNumber, offset} = selection
  const {definitions, localReferences, crossReferences} = codeNavInfo.getTaillightsDefinitionsAndReferences(
    selectedText,
    lineNumber,
    offset,
    async () => {
      const result = await controller.search({query: selectedText, kind: 'symbol', index: 0})
      return codeNavInfo.createReferences([...result.highlights])
    },
  )
  let failed = false
  return {
    ...selection,
    controller,
    key,
    revision,
    get failed() {
      return failed
    },
    definitions: settle(definitions),
    complete: settle(Promise.all([definitions, localReferences, crossReferences]), () => {
      failed = true
    }),
  }
}

function createRequestCache() {
  const controllers = new WeakMap<TaillightsController, WeakMap<Selection['codeNavInfo'], Map<string, Request>>>()
  let nextKey = 0
  return (controller: TaillightsController, selection: Selection, revision: number) => {
    let documents = controllers.get(controller)
    if (!documents) {
      documents = new WeakMap()
      controllers.set(controller, documents)
    }
    let requests = documents.get(selection.codeNavInfo)
    if (!requests) {
      requests = new Map()
      documents.set(selection.codeNavInfo, requests)
    }
    const key = JSON.stringify([selection.selectedText, selection.lineNumber, selection.offset])
    let request = requests.get(key)
    // Retry failures only on a new selection, not on a Suspense render retry.
    if (!request || (request.failed && request.revision !== revision)) {
      request = createRequest(controller, selection, nextKey++, revision)
      requests.set(key, request)
    }
    return request
  }
}

export function TaillightsCodeNavSymbolDetails(props: CodeNavSymbolDetailsLoaderProps) {
  const {codeNavInfo, selectedText, lineNumber, offset} = props
  const controller = useTaillightsController()
  const startupError = useCodeBlobStartupError()
  const [getRequest] = useState(createRequestCache)
  const [available, setAvailable] = useState<{
    controller: TaillightsController
    selection: Selection
    revision: number
  } | null>(null)
  // Keep the displayed selection available while an appearance change reconnects the worker.
  if (
    controller &&
    lineNumber >= 0 &&
    offset >= 0 &&
    (available?.controller !== controller ||
      available.selection.codeNavInfo !== codeNavInfo ||
      available.selection.selectedText !== selectedText ||
      available.selection.lineNumber !== lineNumber ||
      available.selection.offset !== offset)
  ) {
    setAvailable({
      controller,
      selection: {codeNavInfo, selectedText, lineNumber, offset},
      revision: (available?.revision ?? 0) + 1,
    })
  }
  const nextRequest =
    available?.selection.codeNavInfo === codeNavInfo
      ? getRequest(available.controller, available.selection, available.revision)
      : null
  const request = useDeferredValue(nextRequest)

  if (lineNumber < 0 || offset < 0) return <SymbolDetailsStatus message="Symbol location is unavailable." />
  if (startupError) return <SymbolDetailsStatus message={`Unable to load symbol details for ${selectedText}.`} />
  if (!request || request.codeNavInfo !== codeNavInfo) {
    return <SymbolDetailsStatus message={`Loading symbol details for ${selectedText}.`} />
  }

  return (
    <>
      <Suspense fallback={<SymbolDetailsStatus message={`Loading symbol details for ${request.selectedText}.`} />}>
        <ResolvedSymbolDetails onSymbolSelect={props.onSymbolSelect} request={request} />
      </Suspense>
    </>
  )
}

function ResolvedSymbolDetails({
  request,
  onSymbolSelect,
}: Pick<CodeNavSymbolDetailsLoaderProps, 'onSymbolSelect'> & {request: Request}) {
  const definitionResult = use(request.definitions)
  const definitions = definitionResult.status === 'fulfilled' ? definitionResult.value : undefined
  const deferredComplete = useDeferredValue(request.complete, null)
  const completeResult = deferredComplete === request.complete ? use(deferredComplete) : undefined
  const complete = completeResult?.status === 'fulfilled' ? completeResult.value : undefined
  if (definitionResult.status === 'rejected' && request.controller.disposed) {
    return <SymbolDetailsStatus message={`Loading symbol details for ${request.selectedText}.`} />
  }
  if (definitionResult.status === 'rejected') {
    return (
      <ErrorBoundary
        key={request.key}
        boundaryName="taillights-symbol-details"
        fallback={<SymbolDetailsStatus message={`Unable to load symbol details for ${request.selectedText}.`} />}
      >
        <FailedRequest reason={definitionResult.reason} />
      </ErrorBoundary>
    )
  }
  return (
    <>
      <CodeNavSymbolDetailsView
        codeNavInfo={request.codeNavInfo}
        selectedText={request.selectedText}
        onSymbolSelect={onSymbolSelect}
        isLoading={false}
        referencesPending={!complete}
        results={{
          definitions,
          localReferences: complete?.[1],
          crossReferences: complete?.[2],
          error: false,
        }}
      />
      {complete && definitions && (
        <SymbolDetailsAnnouncement
          selectedText={request.selectedText}
          definitions={definitions.definitions.length}
          references={complete[1].references.length + complete[2].references.length}
        />
      )}
      {completeResult?.status === 'rejected' && !request.controller.disposed ? (
        <ErrorBoundary
          key={request.key}
          boundaryName="taillights-symbol-references"
          fallback={<SymbolDetailsStatus message={`Unable to load references for ${request.selectedText}.`} />}
        >
          <FailedRequest reason={completeResult.reason} />
        </ErrorBoundary>
      ) : (
        !complete && <SymbolDetailsStatus message={`Loading references for ${request.selectedText}.`} />
      )}
    </>
  )
}

function SymbolDetailsAnnouncement({
  selectedText,
  definitions,
  references,
}: {
  selectedText: string
  definitions: number
  references: number
}) {
  const message = `${definitions} ${definitions === 1 ? 'definition' : 'definitions'} and ${references} ${references === 1 ? 'reference' : 'references'} for ${selectedText}.`
  useEffect(() => announce(message), [message])
  return null
}

function FailedRequest({reason}: {reason: unknown}): never {
  throw reason
}

function SymbolDetailsStatus({message}: {message: string}) {
  useEffect(() => {
    const timeout = setTimeout(() => announce(message), 300)
    return () => clearTimeout(timeout)
  }, [message])
  return <p className={clsx(detailsStyles.emptyState, styles.Status)}>{message}</p>
}
